/**
 * Identify the Archetype — read a behavioral vignette, pick which of
 * the 6 archetypes from sheets 9–14 it describes.
 *
 * Vignettes are hand-authored to be diagnostic — each one names the
 * observed actions a player would actually log at the table (limp
 * frequencies, showdown specifics, sizing variance, etc.). 4 vignettes
 * per archetype × 6 archetypes = 24 questions.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type Archetype =
  | "station"
  | "nit"
  | "maniac"
  | "limper_whale"
  | "tilted"
  | "solid_reg";

export const ARCHETYPES: Archetype[] = [
  "station",
  "nit",
  "maniac",
  "limper_whale",
  "tilted",
  "solid_reg",
];

export const ARCHETYPE_LABEL: Record<Archetype, string> = {
  station: "Station",
  nit: "Nit",
  maniac: "Maniac",
  limper_whale: "Limper / Whale",
  tilted: "Tilted",
  solid_reg: "Solid Reg",
};

export interface ArchetypeVignette {
  vignette: string;
  archetype: Archetype;
  /** Why this is the archetype — used in the reveal panel. */
  rationale: string;
}

export const IDENTIFY_ARCHETYPE_BANK: ArchetypeVignette[] = [
  // STATION ----------------------------------------------------------
  {
    vignette: "Limped in, called your iso, called the flop with K-high, called the turn second pair, paid off your river value bet showing pocket 9s on a K-7-2-A-3 board.",
    archetype: "station",
    rationale: "Calls-down-light pattern: K-high flop call, second-pair turn call, pays off river without ever raising. Stations don't fold and don't bluff.",
  },
  {
    vignette: "VPIP looks like ~65%, hasn't 3-bet a single time in two hours. Just called bottom pair down 3 streets and showed at the river.",
    archetype: "station",
    rationale: "Passive VPIP through the roof, no aggression, calling bottom pair to showdown: textbook station.",
  },
  {
    vignette: "Hasn't put in a raise this orbit. Calls flop bets light, calls turn lighter, only folds to river bets when they're huge.",
    archetype: "station",
    rationale: "No aggression + sticky calling pattern. Folds only to big polar river bets that are obviously value-heavy.",
  },
  {
    vignette: "Open-limped UTG, called your iso, called your flop bet with no draw, called the turn after you doubled, called the river with ace-high.",
    archetype: "station",
    rationale: "Calls every street with marginal showdown value, never raises. Even ace-high goes to showdown.",
  },

  // NIT --------------------------------------------------------------
  {
    vignette: "VPIP ~8%, PFR ~7%, no 3-bet activity. Folds to a min-raise. Has only put money in with QQ+/AK/AQs in the past hour.",
    archetype: "nit",
    rationale: "Single-digit VPIP and only premiums shown down: the definition of a nit. Folds to any meaningful pressure preflop.",
  },
  {
    vignette: "Two hours at the table, hasn't been in a single 3-bet pot. Just folded BB to a Button open holding ATs.",
    archetype: "nit",
    rationale: "Never 3-bets, folds defensible hands like ATs to a wide BTN open. Tighter than any solver line.",
  },
  {
    vignette: "Open-raised UTG. After a 3-bet from CO, tank-folded JJ face up.",
    archetype: "nit",
    rationale: "Folding JJ to a single 3-bet face-up is a nit tell: they only continue with QQ+/AK in 3-bet pots.",
  },
  {
    vignette: "C-bets only when made hands hit. Checks back top-pair-small-kicker for fear. Folds to second-barrel without thinking.",
    archetype: "nit",
    rationale: "Aggression frequency tied 1:1 to hand strength plus folds to any sustained pressure: the postflop signature of a nit.",
  },

  // MANIAC -----------------------------------------------------------
  {
    vignette: "Opened 5 of the last 6 hands. 3-bet 4 times in the past orbit. Just bluffed 4 streets and showed down 7-high.",
    archetype: "maniac",
    rationale: "Opening 80%+, multi-3-betting per orbit, and tank-bluffing rivers with no equity: extreme aggression frequency with no value backing.",
  },
  {
    vignette: "Bets first every time it's checked to him. Stack swings $0–$1500 every session, no medium results, ever.",
    archetype: "maniac",
    rationale: "Reflexive aggression + variance pattern (boom or bust) is the maniac's signature: they're either stacking the table or felting themselves.",
  },
  {
    vignette: "Raises preflop to 6× with random hands. 3-bets light from BB regularly. Bluff-raises rivers more than once an hour.",
    archetype: "maniac",
    rationale: "Out-of-line sizing (6× opens), light 3-bets from OOP, and frequent river bluff-raises: aggression so wide their bluffs run into nut combos constantly.",
  },
  {
    vignette: "Just open-shoved 50bb preflop with K7o face-up. Has been firing 3 barrels in air-heavy lines for the past 30 minutes.",
    archetype: "maniac",
    rationale: "Open-shoving 50bb with K7o + sustained triple-barreling without showdown value is pure maniac territory.",
  },

  // LIMPER / WHALE ---------------------------------------------------
  {
    vignette: "Open-limped MP 4 hands in a row. Called your iso each time, then called the flop with second pair.",
    archetype: "limper_whale",
    rationale: "Compulsive open-limping + calling isos and continuing with marginal pairs: recreational limp-pattern, not a tactical limp strategy.",
  },
  {
    vignette: "Started the session with $400, now sits with $1800 after a string of suckouts. Plays ~70% of hands, almost all by limping.",
    archetype: "limper_whale",
    rationale: "70% VPIP almost entirely from limping + lucky stack growth from speculative hands hitting: the classic whale profile.",
  },
  {
    vignette: "Limp-calls almost any hand including K-high. Calls flop bets with overcards or any pair. Doesn't raise preflop.",
    archetype: "limper_whale",
    rationale: "Wide limp-call range + passive postflop = whale. They get to a lot of flops cheaply and pay off when they connect.",
  },
  {
    vignette: "First-in raise from her was 8× with K-high in the past hour. Otherwise limps in for $2 every single time.",
    archetype: "limper_whale",
    rationale: "Sizing tell (only raises with a specific hand class, otherwise limps) combined with limp-heavy frequency: a whale who occasionally wakes up with a hand.",
  },

  // TILTED -----------------------------------------------------------
  {
    vignette: "Played tight + balanced for 3 hours. Lost a 100bb stack to a runner-runner suckout 10 minutes ago. Has played the next 4 hands aggressively, 3-betting twice.",
    archetype: "tilted",
    rationale: "Behavior CHANGE after a specific emotional trigger: that's the tilted signature. Their baseline was solid; the cooler shifted them into aggression.",
  },
  {
    vignette: "Just lost a buy-in to a bad beat. Reloaded $500 immediately, open-raised 5× the very next hand, 3-bet the hand after that.",
    archetype: "tilted",
    rationale: "Quick reload + immediate sizing/aggression increase after a loss = monkey-tilt. The behavior is a reaction to the prior hand, not a strategy.",
  },
  {
    vignette: "Was a balanced reg an hour ago. After losing a big pot, started open-jamming 25bb effective.",
    archetype: "tilted",
    rationale: "Open-jamming 25bb is never the baseline strategy of a balanced reg. It's tilt-induced. Behavior change + emotional trigger nails it.",
  },
  {
    vignette: "Caught bluffing big on the river 30 minutes ago. Since then, has been over-folding to aggression AND over-checking strong hands face-up.",
    archetype: "tilted",
    rationale: "Bluff-caught tilt manifests as the opposite: over-folding (afraid to bluff-catch) + over-checking (afraid to value bet). Tilt doesn't always = aggression.",
  },

  // SOLID REG --------------------------------------------------------
  {
    vignette: "Multi-tabling online. VPIP 22% / PFR 18%. Has 4 hours of history showing varied lines + showdowns across hand strengths.",
    archetype: "solid_reg",
    rationale: "On-curve stats + line variety (not always doing the same thing with the same hand class) is the reg signature. Hard to put on a hand because their actions are balanced.",
  },
  {
    vignette: "Plays one table live, takes notes between hands. Open-sizes consistently, 3-bets balanced ranges, c-bets selectively based on board texture.",
    archetype: "solid_reg",
    rationale: "Consistent sizing + texture-aware c-bets + actually studying mid-session = a reg who's actively trying to play correctly.",
  },
  {
    vignette: "8 hours at the table; consistent VPIP, consistent sizing. Showed down a polarized river overbet with the nuts earlier. Clearly thinks about ranges.",
    archetype: "solid_reg",
    rationale: "Range-aware river sizing (polar overbet with nuts) is the calling-card move of a solver-aware reg. They're constructing their range, not just playing their hand.",
  },
  {
    vignette: "Sizes c-bets differently across board textures: small on dry, larger on wet. Doesn't telegraph hand strength via sizing.",
    archetype: "solid_reg",
    rationale: "Texture-dependent sizing without leaking hand strength is the postflop hallmark of a reg. Most rec players use one size or vary by their own hand.",
  },
];

export interface IdentifyArchetypeQuestion {
  vignette: string;
  archetype: Archetype;
  rationale: string;
}

export function generateQuestion(
  rand: () => number = Math.random,
): IdentifyArchetypeQuestion {
  return pickFrom(IDENTIFY_ARCHETYPE_BANK, rand);
}

export const _internals = { IDENTIFY_ARCHETYPE_BANK };
